import React, { useState, useEffect, useRef } from "react";
import Sidebar from "./Sidebar";
import TopPanel from "./TopPanel";
import Footer from "./Footer";
import {
  getAllCampaigns,
  getProducts,
  previewNextCampaignId,
  createCampaign,
  updateCampaignStatus,
} from "../src/Services/campaignService";
import { Link } from "react-router-dom";

const GREEN = "#3b5e40";
const CTL_H = 56; // one height for every input / select / button in the create form
const lbl = { fontSize: 14, fontWeight: 600, marginBottom: 4 };
const cardStyle = { borderRadius: 10, border: "1px solid #e0e0e0" };

const TYPES = [
  { key: "coupon", label: "Coupon / Discount", desc: "Code or CRM-applied price discount" },
  { key: "offer", label: "Offer", desc: "Condition → free product or special price" },
  // { key: "wallet_cashback", label: "Wallet Cashback", desc: "Coming soon", disabled: true },
  { key: "referral", label: "Refer & Earn", desc: "Both earn after the referee qualifies" },
];
const TABS = [
  { id: "All", label: "All" },
  { id: "coupon", label: "Coupons / Discounts" },
  { id: "offer", label: "Offers" },
  // { id: "wallet_cashback", label: "Wallet Cashback" },
  
  { id: "referral", label: "Refer & Earn" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.key, t.label]));
const NAME_LABEL = { coupon: "Coupon name", offer: "Offer name", referral: "Program name" };
const BADGE = {
  coupon: ["#e6f0e9", "#2c5a31"],
  offer: ["#e6f2ff", "#0056b3"],
  wallet_cashback: ["#faebd7", "#a0522d"],
  referral: ["#f3e8ff", "#6b2fa0"],
};
const DOT = { Active: "#28a745", Scheduled: "#007bff", Expired: "#dc3545", Inactive: "#6c757d" };

// Both servers are kept. The first one is tried first; if it fails, the next one is used.
const API_BASES = (() => {
  const host = window.location.hostname;
  if (host === "crm.whytefarms.com") return ["https://app.whytefarms.com"];      // CRM domain -> app.whytefarms
  if (host === "3.22.224.17") return ["http://13.207.176.232:3000"];             // 3.22 IP -> 13.207 IP
  return ["https://app.whytefarms.com", "http://13.207.176.232:3000"];           // localhost / baaki: pehle domain, phir IP
})();
const PERFORMANCE_PATH = "/api/campaigns/performance-list";
const BENEFITS_PATH = "/api/analytics/benefits-this-month";

const fetchWithFallback = async (path, options) => {
  let lastErr;
  for (const base of API_BASES) {
    try {
      const res = await fetch(`${base}${path}`, options);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (e) {
      lastErr = e;
      console.warn(`[fetch] ${base}${path} failed:`, e.message);
    }
  }
  throw lastErr;
};

// accepts [..], { data: [..] }, { campaigns: [..] }, { data: { campaigns: [..] } } ...
const toList = (json) => {
  if (Array.isArray(json)) return json;
  const d = json?.data ?? json?.campaigns ?? json?.result ?? json?.list ?? json?.items;
  if (Array.isArray(d)) return d;
  const inner = d?.campaigns ?? d?.list ?? d?.items ?? d?.rows ?? json?.campaigns ?? json?.list;
  return Array.isArray(inner) ? inner : [];
};
const firstNum = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null && v !== "" && !isNaN(Number(v))) return Number(v);
  return 0;
};
// one normalised row per performance item; ids used to match the campaign
const normalizePerf = (p) => ({
  keys: [p.id, p._id, p.campaignId, p.campaignCode, p.code, p.couponCode].filter(Boolean).map(String),
  name: String(p.name ?? p.campaignName ?? ""),
  customersUsed: firstNum(p.customersUsed, p.usageCount, p.usedCount, p.totalUsage, p.customers, p.stats?.customersUsed),
  benefitGiven: firstNum(
    p.stats?.benefitGiven, p.benefitGiven, p.benefitAmount, p.totalBenefit, p.totalBenefitGiven, p.benefit,
    firstNum(p.discountGiven, p.stats?.discountGiven) + firstNum(p.creditGiven, p.stats?.creditGiven)
  ),
  revenue: firstNum(p.revenue, p.totalRevenue, p.stats?.revenue),
});

// number or null (so "backend did not send it" can be told apart from 0)
const numOrNull = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null && v !== "" && !isNaN(Number(v))) return Number(v);
  return null;
};
// Price comes from products_data (normal price of one pack of the eligible product); discount / final price are derived from it.
// Price is only calculated when exactly ONE eligible product is selected (with several products every product has its own price).
const round2 = (n) => Math.round(n * 100) / 100;
const calcFromProduct = (f, products) => {
  const opts = buildOptions(products);
  const sel = f.productIds.length === 1 ? opts.find((o) => o.key === f.productIds[0]) : null;
  const base = sel?.price ?? null;
  if (f.type === "coupon") {
    const v = Number(f.discountAmount);
    if (!v) return {};
    if (f.discountType === "fixed") {
      return { basePrice: base, discountAmount: base != null ? Math.min(v, base) : v, finalPrice: base != null ? round2(Math.max(base - v, 0)) : null };
    }
    const disc = base != null ? round2((base * v) / 100) : null;
    return { basePrice: base, discountAmount: disc, finalPrice: base != null ? round2(base - disc) : null };
  }
  if (f.type === "offer" && f.rewardType === "special_price") {
    const sp = Number(f.specialPrice);
    if (!sp) return {};
    return { basePrice: base, discountAmount: base != null ? round2(Math.max(base - sp, 0)) : null, finalPrice: sp };
  }
  return {};
};

// ---------- helpers ----------
// today as YYYY-MM-DD in local time (used to block back-dated campaigns)
const todayStr = () => new Date().toLocaleDateString("en-CA");

const toJsDate = (ts) => (ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null);
const fmtDate = (ts) => {
  const d = toJsDate(ts);
  return d ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
};

const displayStatus = (c) => {
  const now = new Date();
  const start = toJsDate(c.startAt);
  const end = toJsDate(c.endAt);
  if (end && end < now) return "Expired";
  if (c.status === "inactive") return "Inactive";
  if (start && start > now) return "Scheduled";
  return "Active";
};


const pid = (p) => p.id || p.productId;
const pname = (p) => p.name || p.productName || "";
const optionsOf = (p) => {
  if (p.packagingOptions?.length) return p.packagingOptions; // all pack sizes
  if (p.qty != null || p.unit) return [{ packaging: p.qty, pkgUnit: p.unit, price: p.price }]; // current getProducts() shape (first pack size)
  if (p.pkgUnit) return [{ packaging: "", pkgUnit: p.pkgUnit, price: p.price }];
  return [{}];
};
const pkgText = (o) => [o.packaging, o.pkgUnit].filter(Boolean).join(" ");
const buildOptions = (products) =>
  products.flatMap((p) =>
    optionsOf(p).map((o, i) => ({
      key: `${pid(p)}::${i}`,
      productId: pid(p),
      name: pname(p),
      pkg: pkgText(o),
      qty: Number(o.packaging) || null, // pack size number, e.g. 1, 500
      unit: o.pkgUnit || "", // pack unit, e.g. "L", "ml"
      price: o.price != null && o.price !== "" && !isNaN(Number(o.price)) ? Number(o.price) : null, // normal price of one pack (products_data)
      label: [pname(p), pkgText(o)].filter(Boolean).join(" "),
    }))
  );
const parseKey = (key) => (key || "").split("::")[0];
// unique product ids from a list of option keys (same product with 2 pack sizes -> 1 id)
const uniqueProductIds = (keys) => [...new Set((keys || []).map(parseKey).filter(Boolean))];
// when every selected option has the same pack size/unit we can multiply "packs x pack size"; otherwise null
const commonOpt = (opts) =>
  opts.length && opts.every((o) => o.qty === opts[0].qty && o.unit === opts[0].unit) ? opts[0] : null;
// count x pack size, e.g. 30 packs of "1 L" -> 30 L ; 4 packs of "500 ml" -> 2000 ml
const calcTotal = (count, opt) => {
  const n = Number(count);
  if (!n) return 0;
  return opt?.qty ? Math.round(n * opt.qty * 1000) / 1000 : n;
};
const totalLabel = (count, opt) => {
  const t = calcTotal(count, opt);
  return t ? `${t}${opt?.unit ? ` ${opt.unit}` : ""}` : "—";
};

// 0 / empty = unlimited
const usageLabel = (v) => (Number(v) > 0 ? `${v} time${Number(v) > 1 ? "s" : ""}` : "Unlimited");

const emptyForm = (type) => ({
  type, name: "", description: "",
  applyMethod: "customer_code", couponCode: "", // "customer_code" | "crm" | "both"
  productIds: [], // eligible products (multi-select): array of option keys
  minDays: 90, customerType: "all", acquisitionSource: "",
  discountType: "percentage", discountAmount: "",
  rewardType: "free_product", rewardProduct: "", specialPrice: "",
  qualifyQtys: {}, // required packs per eligible product: { [optionKey]: packs }
  freeQty: "1", // number of packs (multiplied by the pack size of the selected product)
  referrerAmount: "", refereeAmount: "",
  startDate: "", endDate: "", maxUsage: 1, activeOnStart: true,
});

// Form data -> the shape campaignService.createCampaign() expects
// (campaigns/{campaignId} + config{} + eligibility.productIds[] schema)
const toPayload = (f, activate, products, calc = {}) => {
  const opts = buildOptions(products);
  const optOf = (key) => opts.find((o) => o.key === key);
  const pkgOf = (key) => optOf(key)?.pkg || null;
  const unitOf = (key) => optOf(key)?.unit || "";
  const totalOf = (count, key) => calcTotal(count, optOf(key));

  const selOpts = f.productIds.map(optOf).filter(Boolean);
  const productIds = uniqueProductIds(f.productIds);
  const single = selOpts.length === 1 ? selOpts[0] : null; // pack text / unit only when exactly one product is chosen
  const qtyOpt = commonOpt(selOpts); // shared pack size of all selected products (or null)

  const base = {
    type: f.type,
    name: f.name.trim(),
    description: f.description.trim(),
    activateNow: activate,
    startDate: f.startDate,
    endDate: f.endDate,
  maxUsesPerCustomer: Number(f.maxUsage) > 0 ? Number(f.maxUsage) : 0, 
  };
  const eligibility = {
    productIds,
    packaging: single?.pkg || null,
    unit: single?.unit || "",
    // pack size / unit of every selected product
    packagingOptions: selOpts.map((o) => ({ productId: o.productId, packaging: o.pkg || null, unit: o.unit || "" })),
    minDurationDays: Number(f.minDays),
    customerType: f.customerType,
    acquisitionSources: f.acquisitionSource ? [f.acquisitionSource] : [],
  };

  if (f.type === "coupon") {
    const applyModeMap = { customer_code: "customer", crm: "crm", both: "both" };
    const applyMode = applyModeMap[f.applyMethod] || "code";
    return {
      ...base,
      eligibility,
      config: {
        applyMode, // "code" | "crm" | "both"
        allowAgentApply: applyMode !== "code", // CRM / Both -> true
        discountType: f.discountType === "percentage" ? "percent" : "fixed",
        discountValue: Number(f.discountAmount),
        discountAmount: calc.discountAmount ?? null, // calculated by the backend
        finalPrice: calc.finalPrice ?? null, // calculated by the backend
        // optional: if left empty, the service generates a random code
        code: f.couponCode.trim().toUpperCase(),
      },
    };
  }
  if (f.type === "offer") {
    // Condition: the customer must subscribe to the required quantity of ANY ONE of the eligible products.
    // Every product has its own required quantity (packs x its pack size).
    const qtyOf = (key) => f.qualifyQtys?.[key] ?? "1";
    const items = selOpts.map((o) => ({
      productId: o.productId,
      packaging: o.pkg || null,
      unit: o.unit || "",
      packs: Number(qtyOf(o.key)), // number of packs entered
      value: calcTotal(qtyOf(o.key), o), // total quantity, e.g. 30 packs of 1 L -> 30
    }));
    const condition = {
      kind: "quantity",
      productId: productIds[0] || "", // first product (kept for older readers)
      productIds, // all eligible products
      items, // required quantity per product
      // first product's values, kept for older readers
      packaging: items[0]?.packaging ?? null,
      value: calc.conditionValue ?? items[0]?.value ?? null,
      unit: items[0]?.unit || "",
      packs: items[0]?.packs ?? null,
    };
    // Benefit: how much of the free product is given
    const benefit =
      f.rewardType === "free_product"
        ? { kind: "free_product", productId: parseKey(f.rewardProduct), packaging: pkgOf(f.rewardProduct), qty: calc.benefitQty ?? totalOf(f.freeQty, f.rewardProduct), unit: unitOf(f.rewardProduct), packs: Number(f.freeQty), amount: null }
        : { kind: "special_price", productId: productIds[0] || "", productIds, packaging: qtyOpt?.pkg || null, qty: null, amount: Number(f.specialPrice), finalPrice: calc.finalPrice ?? null, discountAmount: calc.discountAmount ?? null };

    return {
      ...base,
      eligibility,
      config: { condition, benefit, frequency: null },
    };
  }
  // referral
  return {
    ...base,
    eligibility,
    config: {
      referrerReward: Number(f.referrerAmount),
      refereeReward: Number(f.refereeAmount),
      minRecharge: 0,
      subscriptionRequired: true,
      eligibleProductIds: [],
      maxSuccessfulPerReferrer: null,
    },
  };
};

// ---------- small reusable components (kept OUTSIDE the page component, otherwise inputs lose focus while typing) ----------
const Seg = ({ value, options, onChange }) => (
  <div className="btn-group w-100" style={{ flexWrap: "nowrap" }}>
    {options.map(([v, label]) => (
      <button key={v} type="button" className="btn" onClick={() => onChange(v)}
        style={{
          flex: "1 1 0", minWidth: 0, height: CTL_H, padding: "0 0.5rem", fontSize: 15,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: value === v ? GREEN : "#fff", color: value === v ? "#fff" : "#6c757d",
          borderColor: value === v ? GREEN : "#ced4da", boxShadow: "none", whiteSpace: "nowrap",
        }}>
        {label}
      </button>
    ))}
  </div>
);
// col-12 base is required: without it, col-md-* items shrink to content width on narrow screens and overlap
const Field = ({ label, col = 4, hint, children }) => (
  <div className={`col-12 col-md-${col} mb-3`}>
    <label style={{ ...lbl, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minHeight: 21, marginBottom: 6 }}>{label}</label>
    {children}
    {hint}
  </div>
);
const Section = ({ title, children }) => (
  <div className="card shadow-sm mb-4" style={cardStyle}>
    <div className="card-body">
      <h6 style={{ fontWeight: "bold", marginBottom: 15, fontSize: 18 }}>{title}</h6>
      {children}
    </div>
  </div>
);
// Sub-blocks inside the offer form ("Condition" / "Reward")
const Block = ({ title, subtitle, tint, children }) => (
  <div className="mb-3" style={{ border: "1px solid #e0e0e0", borderRadius: 10, padding: "16px 16px 4px", backgroundColor: tint || "#fff" }}>
    <div className="mb-3">
      <div style={{ fontWeight: 700, fontSize: 15, color: "#2c2c2c" }}>{title}</div>
      {subtitle && <div className="text-muted" style={{ fontSize: 12 }}>{subtitle}</div>}
    </div>
    {children}
  </div>
);
const TypeBadge = ({ type }) => {
  const badge = BADGE[type] || ["#eee", "#333"];
  return (
    <span style={{ background: badge[0], color: badge[1], padding: "4px 10px", borderRadius: 15, fontSize: 12, fontWeight: 800 }}>
      {TYPE_LABEL[type] || type || "Unknown"}
    </span>
  );
};
const StatusBadge = ({ status }) => (
  <span style={{ background: "#f1f3f5", color: "#495057", padding: "4px 10px", borderRadius: 15, fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}>
    <span style={{ width: 8, height: 8, background: DOT[status], borderRadius: "50%", display: "inline-block" }} />
    {status}
  </span>
);
const hintStyle = { fontSize: 11, display: "block", marginTop: 4, lineHeight: 1.3 };

// Multi-select dropdown with checkboxes, search and "select all".
// options: [{ key, label }], value: array of selected keys
const MultiSelect = ({ options, value, onChange, placeholder, loadingText }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  // close when clicking outside
  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const filtered = options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()));
  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => value.includes(o.key));
  const selected = options.filter((o) => value.includes(o.key));

  const toggle = (key) => onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
  const toggleAll = () => {
    if (allFilteredSelected) onChange(value.filter((k) => !filtered.some((o) => o.key === k)));
    else onChange([...value, ...filtered.filter((o) => !value.includes(o.key)).map((o) => o.key)]);
  };

  const summary =
    selected.length === 0
      ? options.length ? placeholder : loadingText
      : selected.length === 1
        ? selected[0].label
        : `${selected.length} products selected`;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div
        className="form-select"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        style={{ height: CTL_H, display: "flex", alignItems: "center", cursor: "pointer", color: selected.length ? "#212529" : "#6c757d", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingRight: "2.25rem" }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{summary}</span>
      </div>

      {open && (
        <div style={{ position: "absolute", zIndex: 50, left: 0, right: 0, top: "100%", marginTop: 4, background: "#fff", border: "1px solid #ced4da", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: 8 }}>
          <input
            className="form-control form-control-sm mb-2"
            style={{ height: 38 }}
            placeholder="Search product"
            value={q}
            autoFocus
            onChange={(e) => setQ(e.target.value)}
          />
          <div
            onClick={toggleAll}
            style={{ padding: "6px 8px", cursor: "pointer", fontSize: 14, fontWeight: 700, borderBottom: "1px solid #eee", display: "flex", alignItems: "center" }}
          >
            <input type="checkbox" readOnly checked={allFilteredSelected} className="me-2" />
            {allFilteredSelected ? "Clear all" : q.trim() ? "Select all results" : "Select all"}
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {filtered.length === 0 && <div className="text-muted" style={{ padding: 8, fontSize: 14 }}>No product found</div>}
            {filtered.map((o) => {
              const on = value.includes(o.key);
              return (
                <div
                  key={o.key}
                  onClick={() => toggle(o.key)}
                  style={{ padding: "6px 8px", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", background: on ? "#e8f5e9" : "transparent", borderRadius: 4 }}
                >
                  <input type="checkbox" readOnly checked={on} className="me-2" />
                  {o.label}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mt-2">
          {selected.map((o) => (
            <span key={o.key} style={{ background: "#e8f5e9", color: GREEN, borderRadius: 14, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
              {o.label}
              <span onClick={() => toggle(o.key)} style={{ cursor: "pointer", fontWeight: 700, fontSize: 14, lineHeight: 1 }} title="Remove">×</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Number of packs x the pack size of the selected product, with the total shown below
// e.g. product "Cow Milk 1 L", count 30 -> "× 1 L" and "Total: 30 L"
const PackQty = ({ count, opt, hasProducts, onCount }) => (
  <>
    <div className="input-group flex-nowrap">
      <input type="number" min="1" step="1" className="form-control" style={{ flex: "1 1 auto", minWidth: 0 }} value={count} onChange={(e) => onCount(e.target.value)} />
      <span className="input-group-text" style={{ whiteSpace: "nowrap" }}>{opt ? `× ${opt.pkg || "pack"}` : "× pack"}</span>
    </div>
    <small className="text-muted" style={hintStyle}>
      {opt
        ? <>Total: <strong>{totalLabel(count, opt)}</strong></>
        : hasProducts
          ? "Selected products have different pack sizes, so quantity is counted in packs"
          : "Select a product first"}
    </small>
  </>
);

// Pop-up message (SweetAlert style): no OK button, closes by itself, or when clicked anywhere
const TOAST_COLOR = { success: "#28a745", error: "#dc3545", warning: "#f0a020" };
const TOAST_ICON = { success: "✓", error: "✕", warning: "!" };
const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(onClose, toast.type === "success" ? 2200 : 3200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [toast]);
  if (!toast) return null;
  const color = TOAST_COLOR[toast.type] || TOAST_COLOR.success;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
      <div className="cc-toast" style={{ background: "#fff", borderRadius: 16, padding: "32px 40px", minWidth: 300, maxWidth: 420, textAlign: "center", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}>
        <div className="cc-toast-icon" style={{ width: 80, height: 80, borderRadius: "50%", border: `4px solid ${color}`, color, fontSize: 40, fontWeight: 700, lineHeight: "72px", margin: "0 auto 16px" }}>
          {TOAST_ICON[toast.type]}
        </div>
        <h4 style={{ fontWeight: 700, margin: 0, color: "#2c2c2c", fontSize: 22 }}>{toast.title}</h4>
        {toast.text && <p className="text-muted mt-2 mb-0" style={{ whiteSpace: "pre-line", fontSize: 15 }}>{toast.text}</p>}
      </div>
    </div>
  );
};

function AllCampaigns() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm("coupon"));
  const [campaignId, setCampaignId] = useState("");
  const [toast, setToast] = useState(null);
  const [benefit, setBenefit] = useState(null); // monthly summary (benefits-this-month)
  const [perf, setPerf] = useState([]); // campaign-wise rows (performance-list)
  const [calc, setCalc] = useState(null); // backend-calculated totals / price for the form

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const notify = (type, title, text) => setToast({ type, title, text });

  // ---------- load campaigns and products ----------
  // path is relative; the domain is tried first, then the IP
  const getJson = async (path, label) => {
    const res = await fetchWithFallback(path);
    const json = await res.json();
    console.log(`[${label}] raw response:`, json);
    if (json?.success === false) throw new Error(json.message || `${label}: request failed`);
    return json;
  };
  const loadPerformance = async () => {
    // 1) monthly benefit summary (card)  2) campaign-wise performance (table)
    const [b, p] = await Promise.allSettled([
      getJson(BENEFITS_PATH, "benefits-this-month"),
      getJson(PERFORMANCE_PATH, "performance-list"),
    ]);
    if (b.status === "fulfilled") {
      setBenefit(b.value?.summary || b.value?.data?.summary || null);
      // benefits-this-month may also carry campaign-wise rows
      const rowsFromBenefits = toList(b.value?.data ?? b.value);
      if (p.status !== "fulfilled" && rowsFromBenefits.length) setPerf(rowsFromBenefits.map(normalizePerf));
    } else console.error(b.reason);
    if (p.status === "fulfilled") {
      const list = toList(p.value?.data ?? p.value);
      console.log("[performance-list] first row keys:", list[0] ? Object.keys(list[0]) : "no rows found");
      setPerf(list.map(normalizePerf));
      if (p.value?.summary && b.status !== "fulfilled") setBenefit(p.value.summary);
    } else console.error(p.reason);
    if (b.status === "rejected" && p.status === "rejected") {
      notify("error", "Could not load benefit data", b.reason?.message);
    }
  };
  const loadCampaigns = async () => {
    setLoading(true);
    loadPerformance();
    try {
      setCampaigns(await getAllCampaigns());
    } catch (e) {
      console.error(e);
      notify("error", "Could not load campaigns", e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadCampaigns();
    getProducts().then(setProducts).catch((e) => console.error("Products load error", e));
  }, []);

  // ---------- preview the next ID when the type changes in the create form ----------
  useEffect(() => {
    if (!isCreating) return;
    previewNextCampaignId(form.type).then(setCampaignId).catch(() => setCampaignId(""));
  }, [isCreating, form.type]);

  // ---------- price / discount / final price from products_data ----------
  useEffect(() => {
    setCalc(!isCreating || form.type === "referral" || !form.productIds.length ? null : calcFromProduct(form, products));
    // eslint-disable-next-line
  }, [isCreating, products, form.type, form.productIds, form.rewardType, form.specialPrice, form.discountType, form.discountAmount]);

  // Refer & Earn can be created only once; after that it can be edited (Edit programme) and activated / deactivated
  const hasReferral = campaigns.some((c) => c.type === "referral");

  const openCreate = () => {
    // if a referral campaign already exists, don't let the form open on that type
    setForm(emptyForm("coupon"));
    setIsCreating(true);
  };

  // ---------- Activate / Save as inactive ----------
  const handleSave = async (activate) => {
    const f = form;
    if (f.type === "referral" && hasReferral) {
      return notify(
        "warning",
        "Already Exists",
        "A Refer & Earn campaign already exists.\nOpen it with View and use Edit programme to change it, or activate / deactivate it from this list."
      );
    }
    const isCoupon = f.type === "coupon";
    const isOffer = f.type === "offer";
    const isFreeProduct = f.rewardType === "free_product";
    let err = "";
    if (!f.name.trim()) err = `${NAME_LABEL[f.type]} is required.`;
    else if (f.type !== "referral" && !f.productIds.length) err = "Select at least one eligible product.";
    else if (isCoupon && !(Number(f.discountAmount) > 0)) err = "Enter a discount amount.";
    else if (isCoupon && f.discountType === "percentage" && Number(f.discountAmount) > 100) err = "Percentage cannot exceed 100.";
    else if (isCoupon && f.couponCode && f.couponCode.trim().length < 4) err = "Coupon code must be at least 4 characters (or leave it empty to auto-generate).";
    else if (isOffer && f.productIds.some((k) => !(Number(f.qualifyQtys[k] ?? 1) > 0))) err = "Enter the required quantity for every selected product.";
    else if (isOffer && isFreeProduct && !f.rewardProduct) err = "Select a free product.";
    else if (isOffer && isFreeProduct && !(Number(f.freeQty) > 0)) err = "Enter the free quantity.";
    else if (isOffer && !isFreeProduct && !(Number(f.specialPrice) > 0)) err = "Enter a special price.";
    else if (f.type === "referral" && !(Number(f.referrerAmount) > 0 && Number(f.refereeAmount) > 0)) err = "Enter both reward amounts.";
    else if (!f.startDate || !f.endDate) err = "Select a start and end date.";
    else if (f.startDate < todayStr()) err = "Start date cannot be in the past.";
    else if (f.endDate < f.startDate) err = "End date cannot be before the start date.";
    if (err) return notify("warning", "Missing details", err);

    setSaving(true);
    try {
      const res = await createCampaign(toPayload(f, activate, products, calcFromProduct(f, products)));
      notify("success", "Campaign saved", `ID: ${res.campaignId}` + (res.couponCode ? `\nCode: ${res.couponCode}` : ""));
      setIsCreating(false);
      loadCampaigns();
    } catch (e) {
      console.error(e);
      notify("error", "Could not save campaign", e.message);
    } finally {
      setSaving(false);
    }
  };

  // ---------- Activate / Deactivate button in the list ----------
  const toggleStatus = async (c) => {
    const activating = c.status === "inactive";
    try {
      await updateCampaignStatus(c.id, activating ? "active" : "inactive");
      notify("success", activating ? "Campaign activated" : "Campaign deactivated");
      loadCampaigns();
    } catch (e) {
      notify("error", "Could not update status", e.message);
    }
  };

  // ---------- list filtering ----------
  // merge API performance numbers into each campaign (by id / campaignCode / coupon code, then by name)
  const perfOf = (c) => {
    const ids = [c.id, c.campaignId, c.campaignCode, c.config?.code].filter(Boolean).map(String);
    return perf.find((p) => p.keys.some((k) => ids.includes(k))) || perf.find((p) => p.name && p.name === c.name);
  };
  const withPerf = campaigns.map((c) => {
    const p = perfOf(c);
    return {
      ...c,
      perf: p || {
        customersUsed: c.stats?.customersUsed ?? 0,
        benefitGiven: (c.stats?.discountGiven ?? 0) + (c.stats?.creditGiven ?? 0),
        revenue: c.stats?.revenue ?? 0,
      },
    };
  });
  const rows = withPerf
    .map((c) => ({ ...c, shownStatus: displayStatus(c) }))
    .filter((c) => activeTab === "All" || c.type === activeTab)
    .filter((c) => statusFilter === "All" || c.shownStatus === statusFilter)
    .filter((c) => {
      const q = search.trim().toLowerCase();
      return !q || [c.name, c.campaignCode, c.config?.code].some((x) => (x || "").toLowerCase().includes(q));
    });
  const countOf = (id) => (id === "All" ? campaigns.length : campaigns.filter((c) => c.type === id).length);
  const activeCount = campaigns.filter((c) => displayStatus(c) === "Active").length;
  const totalBenefit = benefit?.totalBenefitGiven != null
    ? Number(benefit.totalBenefitGiven) || 0
    : perf.reduce((s, p) => s + p.benefitGiven, 0);
  const bd = benefit?.breakdown || {};
  const benefitSub = benefit
    ? `Coupons: ₹${(bd.coupons?.benefitAmount ?? 0).toLocaleString("en-IN")} (${bd.coupons?.usageCount ?? 0} uses) · Offers: ₹${(bd.offers?.benefitAmount ?? 0).toLocaleString("en-IN")} (${bd.offers?.usageCount ?? 0} uses)`
    : "";

  // ---------- summary panel (right side) ----------
  const productOptions = buildOptions(products);
  const selectedOpts = productOptions.filter((o) => form.productIds.includes(o.key)); // eligible products chosen
  const selectedOpt = commonOpt(selectedOpts); // shared pack size of the chosen products (null if mixed / none)
  const productName = !selectedOpts.length
    ? "—"
    : selectedOpts.length <= 2
      ? selectedOpts.map((o) => o.label).join(", ")
      : `${selectedOpts[0].label}, +${selectedOpts.length - 1} more`;
  const rewardOpt = productOptions.find((o) => o.key === form.rewardProduct);
  const rewardProductName = rewardOpt?.label || "—";
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const applyMethodLabel = { customer_code: "Customer code", crm: "CRM", both: "Customer code + CRM" };
  const summaryRows = {
    coupon: [
      ["Applied by", applyMethodLabel[form.applyMethod]],
      ["Code", form.couponCode || "Auto-generated"],
      ["Products", productName], ["Min duration", `${form.minDays} days`], ["Customers", cap(form.customerType)],
      ["Discount", form.discountAmount ? (form.discountType === "percentage" ? `${form.discountAmount}%` : `₹${form.discountAmount}`) : "—"],
      ...(calc?.basePrice != null ? [["Normal price", `₹${calc.basePrice}`]] : []),
      ...(calc?.discountAmount != null ? [["Discount amount", `₹${calc.discountAmount}`]] : []),
      ...(calc?.finalPrice != null ? [["Customer pays", `₹${calc.finalPrice}`]] : []),
      ["Per customer", usageLabel(form.maxUsage)],
    ],
    offer: [
      ["Products", productName],
      ...(selectedOpts.length
        ? selectedOpts.map((o) => [`Required · ${o.label}`, totalLabel(form.qualifyQtys[o.key] ?? "1", o)])
        : [["Required qty", "—"]]),
      ["Min duration", `${form.minDays} days`], ["Customers", cap(form.customerType)],
      ["Reward", form.rewardType === "free_product"
        ? `${calc?.benefitQty != null ? `${calc.benefitQty}${rewardOpt?.unit ? ` ${rewardOpt.unit}` : ""}` : form.freeQty ? totalLabel(form.freeQty, rewardOpt) : "—"} · ${rewardProductName}`
        : form.specialPrice ? `₹${form.specialPrice} special price` : "—"],
      ...(form.rewardType === "special_price" && calc?.discountAmount != null ? [["You save", `₹${calc.discountAmount}`]] : []),
      ["Per customer", usageLabel(form.maxUsage)],
    ],
    referral: [
      ["Qualifies after", `${form.minDays} days`], ["Referrer earns", form.referrerAmount ? `₹${form.referrerAmount}` : "—"],
      ["Referee earns", form.refereeAmount ? `₹${form.refereeAmount}` : "—"], ["Per customer", usageLabel(form.maxUsage)],
    ],
  }[form.type] || [];

  // Single product dropdown (used for the "free product" reward): every option shows the name with its quantity, e.g. "Cow's Milk 1 L"
  const productSelect = (value, onChange) => (
    <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{products.length ? "Select product" : "Loading products..."}</option>
      {productOptions.map((o) => (
        <option key={o.key} value={o.key}>{o.label}</option>
      ))}
    </select>
  );

  // Eligible products: multi-select (coupon + offer)
  const eligibleProductsSelect = () => (
    <MultiSelect
      options={productOptions}
      value={form.productIds}
      onChange={(v) => set("productIds", v)}
      placeholder="Select products"
      loadingText="Loading products..."
    />
  );

  const durationField = (col = 4) => (
    <Field label={form.type === "referral" ? "Referee must subscribe for" : "Minimum duration"} col={col}>
      <select className="form-select" value={form.minDays} onChange={(e) => set("minDays", e.target.value)}>
        <option value={30}>1 month</option>
        <option value={90}>3 months</option>
        <option value={180}>6 months</option>
      </select>
    </Field>
  );
  const customerFields = (typeCol = 4, srcCol = 4) => (
    <>
      <Field label="Customer type" col={typeCol}>
        <Seg value={form.customerType} onChange={(v) => set("customerType", v)} options={[["new", "New"], ["existing", "Existing"], ["all", "All"]]} />
      </Field>
      <Field label="Acquisition source" col={srcCol}>
        <select className="form-select" value={form.acquisitionSource} onChange={(e) => set("acquisitionSource", e.target.value)}>
          <option value="">Any source</option>
        </select>
      </Field>
    </>
  );

  return (
    <>
      <style>{`
        .cc-form input.form-control,
        .cc-form select.form-select,
        .cc-form .input-group-text { height: ${CTL_H}px; }
        .cc-form select.form-select { padding-right: 2.25rem; }
        @keyframes ccPop { 0% { transform: scale(.7); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        @keyframes ccIcon { 0% { transform: scale(0); } 60% { transform: scale(1.15); } 100% { transform: scale(1); } }
        .cc-toast { animation: ccPop .2s ease-out; }
        .cc-toast-icon { animation: ccIcon .35s ease-out; }
      `}</style>

      <Toast toast={toast} onClose={() => setToast(null)} />

      {(loading || saving) && (
        <div className="loader-overlay"><div className="spinner-border" role="status" style={{ width: "3.5rem", height: "3.5rem", color: GREEN }} /></div>
      )}
      <div className="container-scroller" style={{ backgroundColor: "#f9f7f2" }}>
        <TopPanel />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel" style={{ backgroundColor: "#f9f7f2", minHeight: "100vh" }}>
            <div className="content-wrapper" style={{ padding: "2rem" }}>
              {!isCreating ? (
                /* ================= DASHBOARD VIEW ================= */
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <h2 style={{ fontWeight: 700, fontSize: 32, fontFamily: "serif", color: "#2c2c2c" }}>All Campaigns</h2>
                    <button className="btn" style={{ backgroundColor: GREEN, color: "#fff", fontWeight: 650, borderRadius: 10, padding: "8px 20px" }} onClick={openCreate}>
                      + Create Campaign
                    </button>
                  </div>

                  <div className="row mb-4">
                    {[
                      ["Active campaigns", activeCount, "#2c2c2c", ""],
                      ["Benefit given · this month", `₹${totalBenefit.toLocaleString("en-IN")}`, "#a56627", benefitSub],
                    ].map(([t, v, color, sub]) => (
                      <div className="col-12 col-md-6 mb-3" key={t}>
                        <div className="card shadow-sm h-100" style={{ borderRadius: 15, border: "1px solid lightgray", minHeight: 160 }}>
                          <div className="card-body py-5">
                            <p className="text-muted mb-2" style={{ fontSize: 18 }}>{t}</p>
                            <h3 style={{ fontWeight: "bold", fontSize: 40, margin: 0, color }}>{v}</h3>
                            {sub && <p className="text-muted mb-0 mt-2" style={{ fontSize: 14 }}>{sub}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="card shadow-sm" style={cardStyle}>
                    <div className="card-body p-0">
                      <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-3" style={{ backgroundColor: "#fff" }}>
                        <div className="d-flex gap-4 flex-wrap">
                          {TABS.map((tab) => {
                            const on = activeTab === tab.id;
                            return (
                              <div key={tab.id} onClick={() => setActiveTab(tab.id)}
                                style={{ textAlign: "center", borderBottom: `3px solid ${on ? GREEN : "transparent"}`, paddingBottom: 5, cursor: "pointer", fontWeight: on ? "bold" : "normal", color: on ? "#2c2c2c" : "#6c757d", fontSize: 16, whiteSpace: "nowrap" }}>
                                <div>{tab.label}</div>
                                <div>{countOf(tab.id)}</div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="d-flex gap-3 flex-wrap">
                          <div>
                            <label className="text-muted" style={{ fontSize: 14, display: "block", marginBottom: 2 }}>Status</label>
                            <select className="form-select form-select-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                              {["All", "Active", "Scheduled", "Inactive", "Expired"].map((s) => <option key={s}>{s}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-muted" style={{ fontSize: 14, display: "block", marginBottom: 2 }}>Search</label>
                            <input className="form-control form-control-sm" placeholder="Name, ID or code" value={search} onChange={(e) => setSearch(e.target.value)} />
                          </div>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover mb-0" style={{ fontSize: 14 }}>
                          <thead style={{ backgroundColor: "#f8f9fa", color: "#6c757d", fontSize: 12 }}>
                            <tr>
                              <th className="py-3 ps-4">CAMPAIGN</th><th className="py-3">TYPE</th><th className="py-3">START</th><th className="py-3">END</th>
                              <th className="py-3">STATUS</th><th className="py-3 text-end">CUSTOMERS USED</th><th className="py-3 text-end">BENEFIT GIVEN</th>
                              <th className="py-3 text-center">ACTIONS</th>
                              <th className="py-3 text-center">VIEW</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.length === 0 && (
                              <tr><td colSpan={10} className="text-center text-muted py-5">
                                {loading ? "Loading..." : "No campaigns found. Use \"+ Create Campaign\" to add your first one."}
                              </td></tr>
                            )}
                            {rows.map((c) => (
                              <tr key={c.id} style={{ verticalAlign: "middle" }}>
                                <td className="ps-4">
                                  <div style={{ fontWeight: "bold", color: "#343a40" }}>{c.name}</div>
                                  <div style={{ fontSize: 13, color: "#a0a0a0", marginTop: 8 }}>
                                    {c.campaignCode || c.id}{c.config?.code ? ` · ${c.config.code}` : ""}
                                  </div>
                                </td>
                                <td><TypeBadge type={c.type} /></td>
                                <td style={{ fontWeight: 700 }}>{fmtDate(c.startAt)}</td>
                                <td style={{ fontWeight: 700 }}>{fmtDate(c.endAt)}</td>
                                <td><StatusBadge status={c.shownStatus} /></td>
                                <td className="text-end">{c.perf.customersUsed}</td>
                                <td className="text-end" style={{ color: "#a56627", fontWeight: 700 }}>₹{c.perf.benefitGiven.toLocaleString("en-IN")}</td>
                              
                                <td className="text-center">
                                  {c.shownStatus !== "Expired" && (
                                    <button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: 16, fontSize: 13, color: "black" }} onClick={() => toggleStatus(c)}>
                                      {c.status === "inactive" ? "Activate" : "Deactivate"}
                                    </button>
                                  )}
                                </td>
                                <td className="text-center">
                                  <Link
                                    to={c.type === "referral" ? "/refer-earn" : `/coupons-discount-details/${c.id}`}
                                    className="btn btn-sm btn-outline-success"
                                    style={{ borderRadius: 16, fontSize: 13, textDecoration: "none", color: GREEN, fontWeight: 600 }}
                                  >
                                    View
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* ================= CREATE CAMPAIGN VIEW ================= */
                <div className="cc-form">
                  <div className="mb-4">
                    <span style={{ color: GREEN, cursor: "pointer", fontWeight: 600, fontSize: 16 }} onClick={() => setIsCreating(false)}>← All Campaigns</span>
                    <h2 className="mt-2" style={{ fontWeight: 700, fontFamily: "serif", color: "#2c2c2c", fontSize: 32 }}>Create Campaign</h2>
                  </div>

                  <div className="row">
                    <div className="col-12 col-lg-8">
                      <Section title="1 · Campaign type">
                        <div className="d-flex gap-3 flex-wrap">
                          {TYPES.map((t) => {
                            const on = form.type === t.key;
                            const locked = t.disabled || (t.key === "referral" && hasReferral);
                            const desc = t.key === "referral" && hasReferral
                              ? "Already created. Use Edit programme to change it"
                              : t.desc;
                            return (
                              <div key={t.key} onClick={() => !locked && setForm(emptyForm(t.key))}
                                style={{ border: on ? `2px solid ${GREEN}` : "1px solid #ddd", borderRadius: 8, padding: 15, flex: "1 1 220px", minWidth: 180, backgroundColor: on ? "#f9fcf9" : "transparent", cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1 }}>
                                <h6 style={{ fontWeight: "bold", margin: 0, color: on ? GREEN : "#343a40" }}>{t.label}</h6>
                                <p style={{ fontSize: 13, color: "#6c757d", margin: "5px 0 0 0" }}>{desc}</p>
                              </div>
                            );
                          })}
                        </div>
                      </Section>

                      <Section title="2 · Basics">
                        <div className="row">
                          <Field label={NAME_LABEL[form.type]} col={6}>
                            <input className="form-control" value={form.name} onChange={(e) => set("name", e.target.value)} />
                          </Field>
                          <Field label="Internal Campaign ID" col={6} hint={<small className="text-muted" style={hintStyle}>Assigned automatically on save</small>}>
                            <input className="form-control" value={campaignId} readOnly style={{ backgroundColor: "#f1f3f5" }} />
                          </Field>
                        </div>
                        <label style={lbl}>Description</label>
                        <textarea className="form-control" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
                      </Section>

                      <Section title={form.type === "referral" ? "3 · Qualification & rewards" : "3 · Eligibility & " + (form.type === "offer" ? "reward" : "discount")}>
                        {/* ---------- COUPON ---------- */}
                        {form.type === "coupon" && (
                          <>
                            <label style={lbl}>How is it applied?</label>
                            <div className="d-flex gap-3 mb-3 flex-wrap align-items-center" style={{ fontSize: 13 }}>
                              {[
                                ["customer_code", "Customer enters code"],
                                ["crm", "Applied from CRM"],
                                ["both", "Both (Customer code + CRM)"],
                              ].map(([v, t]) => (
                                <div key={v} onClick={() => set("applyMethod", v)}
                                  style={{ border: "1px solid #ccc", borderRadius: 8, padding: "10px 15px", cursor: "pointer", backgroundColor: form.applyMethod === v ? "#e8f5e9" : "white" }}>
                                  <input type="radio" readOnly checked={form.applyMethod === v} className="me-2" />{t}
                                </div>
                              ))}
                            </div>

                            <div className="row">
                              <Field label="Coupon code (optional)" hint={<small className="text-muted" style={hintStyle}>Leave empty to auto-generate a code on save</small>}>
                                <input
                                  className="form-control"
                                  value={form.couponCode}
                                  maxLength={20}
                                  placeholder="e.g. MILK3M10"
                                  style={{ textTransform: "uppercase" }}
                                  onChange={(e) => set("couponCode", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                                />
                              </Field>
                              <Field label="Eligible products">{eligibleProductsSelect()}</Field>
                              {durationField()}
                            </div>
                            <div className="row">
                              <Field label="Discount type">
                                <Seg value={form.discountType} onChange={(v) => set("discountType", v)} options={[["fixed", "Fixed ₹"], ["percentage", "Percentage %"]]} />
                              </Field>
                              <Field label="Discount amount">
                                <div className="input-group flex-nowrap">
                                  <input type="number" min="0" className="form-control" style={{ minWidth: 0 }} value={form.discountAmount} onChange={(e) => set("discountAmount", e.target.value)} />
                                  <span className="input-group-text">{form.discountType === "percentage" ? "%" : "₹"}</span>
                                </div>
                              </Field>
                            </div>
                            <div className="row">{customerFields(4, 8)}</div>
                            <div className="alert mt-2 mb-0" style={{ backgroundColor: "#e6f2ff", color: "#0056b3", fontSize: 14, borderRadius: 8 }}>
                              Only one discount applies per subscription. Wallet cashback and referral credit can still be earned alongside it.
                            </div>
                          </>
                        )}

                        {/* ---------- OFFER ---------- */}
                        {form.type === "offer" && (
                          <>
                            <Block title="When does the offer apply?" subtitle="The customer must meet this condition for any of the selected products">
                              <div className="row">
                                <Field label="Eligible products" col={12}>
                                  {eligibleProductsSelect()}
                                </Field>
                                <Field label="Required quantity (per product)" col={12}>
                                  {selectedOpts.length === 0 ? (
                                    <small className="text-muted" style={hintStyle}>Select the eligible products above first</small>
                                  ) : (
                                    <div style={{ border: "1px solid #e0e0e0", borderRadius: 8, background: "#fff" }}>
                                      {selectedOpts.map((o, i) => (
                                        <div
                                          key={o.key}
                                          className="d-flex align-items-start gap-3 flex-wrap"
                                          style={{ padding: "12px 14px", borderBottom: i < selectedOpts.length - 1 ? "1px solid #eee" : "none" }}
                                        >
                                          <div style={{ flex: "1 1 180px", fontWeight: 600, fontSize: 14, paddingTop: 16 }}>{o.label}</div>
                                          <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                                            <PackQty
                                              count={form.qualifyQtys[o.key] ?? "1"}
                                              opt={o}
                                              hasProducts
                                              onCount={(v) => set("qualifyQtys", { ...form.qualifyQtys, [o.key]: v })}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </Field>
                              </div>
                              <div className="row">
                                {durationField(4)}
                                {customerFields(4, 4)}
                              </div>
                            </Block>

                            <Block title="What does the customer get?" subtitle="Reward given once the condition is met" tint="#f9fcf9">
                              <div className="row">
                                <Field label="Reward type" col={12}>
                                  <Seg value={form.rewardType} onChange={(v) => set("rewardType", v)} options={[["free_product", "Free product"], ["special_price", "Special price"]]} />
                                </Field>
                              </div>
                              {form.rewardType === "free_product" ? (
                                <div className="row">
                                  <Field label="Free product" col={6}>
                                    {productSelect(form.rewardProduct, (v) => set("rewardProduct", v))}
                                  </Field>
                                  <Field label="Free quantity" col={6}>
                                    <PackQty count={form.freeQty} opt={rewardOpt} hasProducts={!!form.rewardProduct} onCount={(v) => set("freeQty", v)} />
                                  </Field>
                                </div>
                              ) : (
                                <div className="row">
                                  <Field
                                    label="Special price per pack (₹)"
                                    col={6}
                                    hint={
                                      <small className="text-muted" style={hintStyle}>
                                        {selectedOpts.length === 1
                                          ? <>Customer pays this price for each <strong>{selectedOpts[0].label}</strong> instead of the normal price</>
                                          : selectedOpts.length > 1
                                            ? "Customer pays this price for each pack of the selected products instead of the normal price"
                                            : "Select the eligible products above first"}
                                      </small>
                                    }
                                  >
                                    <div className="input-group flex-nowrap">
                                      <span className="input-group-text">₹</span>
                                      <input type="number" min="0" className="form-control" style={{ minWidth: 0 }} placeholder="e.g. 15" value={form.specialPrice} onChange={(e) => set("specialPrice", e.target.value)} />
                                      <span className="input-group-text" style={{ whiteSpace: "nowrap" }}>{selectedOpt?.pkg ? `/ ${selectedOpt.pkg}` : "/ pack"}</span>
                                    </div>
                                  </Field>
                                </div>
                              )}
                            </Block>
                          </>
                        )}

                        {/* ---------- REFERRAL ---------- */}
                        {form.type === "referral" && (
                          <div className="row">
                            {durationField()}
                            <Field label="Referrer earns (₹)">
                              <input type="number" min="0" className="form-control" value={form.referrerAmount} onChange={(e) => set("referrerAmount", e.target.value)} />
                            </Field>
                            <Field label="Referee earns (₹)">
                              <input type="number" min="0" className="form-control" value={form.refereeAmount} onChange={(e) => set("refereeAmount", e.target.value)} />
                            </Field>
                          </div>
                        )}
                      </Section>

                      <Section title="4 · Validity & limits">
                        <div className="row">
                          <Field label="Start date"><input type="date" className="form-control" min={todayStr()} value={form.startDate} onChange={(e) => set("startDate", e.target.value)} /></Field>
                          <Field label="End date"><input type="date" className="form-control" min={form.startDate || todayStr()} value={form.endDate} onChange={(e) => set("endDate", e.target.value)} /></Field>
                          <Field label="Max usage per customer">
                            <select className="form-select" value={form.maxUsage} onChange={(e) => set("maxUsage", e.target.value)}>
                              <option value={1}>1 time (recommended)</option>
                              <option value={2}>2 times</option>
                              <option value={3}>3 times</option>
                               <option value={0}>Unlimited</option>
                            </select>
                          </Field>
                        </div>
                      </Section>
                    </div>

                    {/* Right summary */}
                    <div className="col-12 col-lg-4">
                      <div className="card shadow-sm position-sticky" style={{ ...cardStyle, top: 20 }}>
                        <div className="card-body">
                          <p className="text-muted mb-2" style={{ fontSize: 13, fontWeight: "bold" }}>SUMMARY</p>
                          <TypeBadge type={form.type} />
                          <h4 className="mt-3" style={{ fontWeight: "bold", fontFamily: "serif", fontSize: 22 }}>{form.name || "Untitled campaign"}</h4>
                          <div className="mt-3 mb-4" style={{ fontSize: 14 }}>
                            {summaryRows.map(([k, v]) => (
                              <div key={k} className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted">{k}</span><span style={{ fontWeight: 600, textAlign: "right" }}>{v}</span>
                              </div>
                            ))}
                          </div>
                          <div className="mb-4">
                            <input type="checkbox" id="activeStart" checked={form.activeOnStart} onChange={(e) => set("activeOnStart", e.target.checked)} className="me-2" />
                            <label htmlFor="activeStart" style={{ fontSize: 13, fontWeight: "bold", cursor: "pointer" }}>Active on start date</label>
                          </div>
                          <button className="btn w-100 mb-2" disabled={saving} onClick={() => handleSave(true)}
                            style={{ backgroundColor: GREEN, color: "#fff", fontWeight: "bold", padding: 12, borderRadius: 8 }}>
                            {saving ? "Saving..." : "Activate campaign"}
                          </button>
                          <button className="btn w-100 save-inactive-btn" disabled={saving} onClick={() => handleSave(false)}
                            style={{ fontWeight: "bold", padding: 12, borderRadius: 8, border: "1px solid #ccc" }}>
                            Save as inactive
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}

export default AllCampaigns;